import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import {
  ApiOutlined,
  ExclamationCircleFilled,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ConfigPanel } from "../panel/ConfigPanel";
import { PANEL_ACCENTS } from "../panel/panelAccents";
import { useEdgeState } from "../../../../hooks/useEdgeMode";
import {
  EDGE_PRESENCE_STALE_MS,
  getEdgeConfiguration,
  updateEdgeConfiguration,
} from "../../../../api/edge";
import {
  EDGE_PREPARE_FEEDBACK,
  prepareThisDevice,
  probeEdgeReachability,
  refreshEdgeSettings,
} from "../../../../lib/edge/edgeSession";
import { getApiErrorMessage } from "../../../../utils/translations";

/**
 * „Локален резерв" в Конфигурация → Устройства (docs/edge.md).
 *
 * Панелът казва ДВЕ различни неща и затова е на две части:
 *
 * - **Обектът** — резервът е ЕДИН за ресторанта (`PUT /configuration/edge`,
 *   право `manage_devices`): включен ли е, коя е главната каса, колко
 *   устройства са я ползвали, мълчи ли. „Изключи" пита изрично — натиснат от
 *   който и да е терминал, спира резерва за ВСИЧКИ.
 * - **Това устройство** — разрешението за локалната мрежа се дава на всеки
 *   терминал и монитор поотделно: „Подготви това устройство" и „Провери".
 *
 * Дотогава ключът на обекта стоеше до бутона на устройството и всеки терминал
 * изглеждаше като място, където резервът се „включва за този компютър". Когато
 * всичко е наред (включен, тук е готово, нищо не чака), панелът се свива до
 * един ред — подробностите са на един клик.
 */

/** Готовността от `GET /configuration/edge` — своя заявка, защото Redux носи
 * само това, от което зависи РАБОТАТА (адрес и режим), не картината. */
const READINESS_REFETCH_MS = 60_000;

type SiteState = "disabled" | "noMaster" | "localNow" | "enabled";
type DeviceState = "localNow" | "unknown" | "ready" | "unreachable";

const CHIP_CLASS = {
  ok: "border-ok-tint2 bg-ok-tint text-ok-deep",
  danger: "border-danger-tint2 bg-danger-tint text-danger-ink",
  warn: "border-warn-tint2 bg-warn-tint text-warn-ink",
  neutral: "border-shade-3 bg-fill-2 text-soft",
} as const;

const SITE_TONE: Record<SiteState, keyof typeof CHIP_CLASS> = {
  enabled: "ok",
  noMaster: "danger",
  localNow: "warn",
  disabled: "neutral",
};

const DEVICE_TONE: Record<DeviceState, keyof typeof CHIP_CLASS> = {
  ready: "ok",
  unreachable: "danger",
  localNow: "warn",
  unknown: "neutral",
};

const toolButtonClass =
  "inline-flex h-[38px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[10px] border px-4 text-[13px] font-bold tracking-[-0.2px] transition-colors disabled:cursor-not-allowed disabled:opacity-45";
const ghostButtonClass = `${toolButtonClass} border-shade-3 bg-surface text-body hover:bg-fill`;
const accentButtonClass = `${toolButtonClass} border-violet-tint2 bg-violet-tint text-violet hover:bg-violet-tint2`;

function StateChip({
  tone,
  label,
  testId,
}: {
  tone: keyof typeof CHIP_CLASS;
  label: string;
  testId: string;
}) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex h-7 shrink-0 items-center self-start whitespace-nowrap rounded-full border px-3 text-[11.5px] font-bold uppercase tracking-[.06em] ${CHIP_CLASS[tone]}`}
    >
      {label}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-bold uppercase tracking-[1.2px] text-muted">
      {children}
    </div>
  );
}

export default function EdgeStatusPanel({ isNarrow }: { isNarrow: boolean }) {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const edge = useEdgeState();
  const [checking, setChecking] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const address = edge.ip ? `${edge.ip}:${edge.port}` : null;

  // Полетата на готовността са по избор: по-стар BE или каса от по-ранен билд
  // просто не ги праща и панелът мълчи за тях — никога „0 устройства".
  const readiness = useQuery({
    queryKey: ["configuration", "edge", "readiness"],
    queryFn: getEdgeConfiguration,
    // Обект без резерв няма готовност, която да се следи — заявката тръгва
    // чак когато той бъде включен (Redux го научава от същия endpoint).
    enabled: edge.enabled,
    refetchInterval: READINESS_REFETCH_MS,
    retry: 1,
  });
  const clientsSeen = readiness.data?.clients_seen ?? null;
  const pendingEvents = readiness.data?.pending_events ?? 0;
  // Неразрешените ги знаят двете страни: касата казва на ТОЗИ терминал (Redux,
  // само в локален режим), а облакът — през presence-а. По-голямото е истината.
  const unresolved = Math.max(
    edge.unresolved,
    readiness.data?.unresolved_events ?? 0,
  );
  const lastSeenAt = readiness.data?.last_seen_at
    ? new Date(readiness.data.last_seen_at)
    : null;
  const presenceStale =
    lastSeenAt !== null &&
    Date.now() - lastSeenAt.getTime() > EDGE_PRESENCE_STALE_MS;

  const siteState: SiteState = !edge.enabled
    ? "disabled"
    : edge.mode === "local" || edge.cloudMode === "local"
      ? "localNow"
      : !edge.ip
        ? "noMaster"
        : "enabled";

  const deviceState: DeviceState =
    edge.mode === "local"
      ? "localNow"
      : edge.edgeReachable === null
        ? "unknown"
        : edge.edgeReachable
          ? "ready"
          : "unreachable";

  // Устройството има какво да подготвя само срещу обявена каса на включен обект.
  const deviceApplies = edge.enabled && !!edge.ip;

  // Всичко е наред и нищо не чака човек → един ред. Всяко предупреждение
  // (мълчаща каса, неразрешени, нито едно устройство) държи панела разгънат —
  // затова и свиването чака готовността: преди нея „0 устройства" не се знае.
  const compactable =
    readiness.isFetched &&
    siteState === "enabled" &&
    deviceState === "ready" &&
    unresolved === 0 &&
    !presenceStale &&
    clientsSeen !== 0;
  const compact = compactable && !expanded;

  const updateMutation = useMutation({
    mutationFn: updateEdgeConfiguration,
    // Отговорът на PUT-а не се слива ръчно: edgeMode дърпа конфигурацията
    // наново, издава билет и сондира касата — един път за всички случаи.
    onSuccess: async () => {
      await refreshEdgeSettings();
      await queryClient.invalidateQueries({ queryKey: ["configuration", "edge"] });
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, t("edge.updateError")));
    },
  });

  const handleCheck = async () => {
    setChecking(true);
    try {
      await probeEdgeReachability();
    } finally {
      setChecking(false);
    }
  };

  /** Едно натискане = всичко, което ТОВА устройство иска, за да работи офлайн. */
  const handlePrepare = async () => {
    setPreparing(true);
    try {
      const { key, tone } = EDGE_PREPARE_FEEDBACK[await prepareThisDevice()];
      message[tone](t(key));
    } finally {
      setPreparing(false);
    }
  };

  const handleEnable = () =>
    updateMutation.mutate({ enabled: true, force_cloud: false });

  // Изключването важи за ЦЕЛИЯ обект, натиснато от който и да е терминал —
  // затова въпрос, а не мигновен бутон до „Подготви това устройство".
  const handleDisable = () => {
    modal.confirm({
      title: t("edge.disableTitle"),
      content: t("edge.disableQuestion"),
      icon: (
        <ExclamationCircleFilled
          style={{ color: "var(--color-brand-deep-ink)" }}
        />
      ),
      centered: true,
      okText: t("edge.disableConfirm"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      // Грешката я казва `onError` на мутацията; прозорецът се затваря.
      onOk: () =>
        updateMutation
          .mutateAsync({ enabled: false, force_cloud: false })
          .catch(() => undefined),
    });
  };

  const siteLabel = {
    disabled: t("edge.stateDisabled"),
    noMaster: t("edge.stateNoMaster"),
    localNow: t("edge.stateLocalNow"),
    enabled: t("edge.stateEnabled"),
  }[siteState];

  const siteHint = {
    disabled: t("edge.hintDisabled"),
    noMaster: t("edge.hintNoMaster"),
    localNow: t("edge.cloudModeLocal"),
    enabled: t("edge.siteEnabledHint"),
  }[siteState];

  const deviceLabel = {
    localNow: t("edge.stateLocalNow"),
    unknown: t("edge.stateUnknown"),
    ready: t("edge.stateReady"),
    unreachable: t("edge.stateUnreachable"),
  }[deviceState];

  const deviceHint = {
    localNow: t("edge.hintLocalNow"),
    unknown: t("edge.hintUnknown"),
    ready: t("edge.hintReady", { address: address ?? "—" }),
    unreachable: t("edge.hintUnreachable", { address: address ?? "—" }),
  }[deviceState];

  const sectionPad = isNarrow ? "px-3.5 py-3.5" : "px-5 py-4";
  const sectionRow = `flex gap-4 ${isNarrow ? "flex-col" : "items-start"}`;

  const clientsLine =
    edge.enabled && clientsSeen !== null ? (
      <p className="m-0 text-[12.5px] font-semibold text-body">
        {clientsSeen === 0
          ? t("edge.clientsSeenNone")
          : t("edge.clientsSeen", { count: clientsSeen })}
      </p>
    ) : null;

  return (
    <ConfigPanel
      scope="dev-edge"
      accent={PANEL_ACCENTS.violet}
      icon={<ApiOutlined />}
      kicker={t("edge.panelKicker")}
      title={t("edge.panelTitle")}
      isNarrow={isNarrow}
      toolbar={
        compactable ? (
          <button
            type="button"
            data-testid="edge-panel-toggle"
            className={ghostButtonClass}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? t("edge.hideDetails") : t("edge.showDetails")}
          </button>
        ) : undefined
      }
    >
      {compact ? (
        <div className={`${sectionPad} ${sectionRow}`} data-testid="edge-panel-compact">
          <StateChip tone="ok" label={t("edge.stateReady")} testId="edge-site-state" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="m-0 text-[13px] font-semibold leading-[1.45] text-body">
              {t("edge.compactSummary")}
            </p>
            {clientsLine}
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          {/* ── Обектът ─────────────────────────────────────────────── */}
          <section className={`${sectionPad} flex flex-col gap-2.5`} data-testid="edge-site">
            <SectionLabel>{t("edge.sectionSite")}</SectionLabel>
            <div className={sectionRow}>
              <StateChip tone={SITE_TONE[siteState]} label={siteLabel} testId="edge-site-state" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <p className="m-0 text-[13px] leading-[1.45] text-body">{siteHint}</p>
                <p className="m-0 text-[12px] text-muted">{t("edge.siteOnceHint")}</p>
                {/* Готова каса ≠ готов обект: числото е единственото
                    доказателство, че терминалите и мониторите са минали
                    подготовката си. */}
                {clientsLine}
                {/* Мълчаща каса: иначе никой не научава, че е спряла преди часове. */}
                {edge.enabled && presenceStale && lastSeenAt && (
                  <p className="m-0 text-[12px] font-semibold text-warn-ink">
                    {t("edge.presenceStale", {
                      time: lastSeenAt.toLocaleTimeString(i18n.language, {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })}
                  </p>
                )}
                {edge.enabled && pendingEvents > 0 && (
                  <p className="m-0 text-[12px] text-muted">
                    {t("edge.pendingEvents", { count: pendingEvents })}
                  </p>
                )}
                {(address || edge.masterClientId) && (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-muted">
                    {edge.masterClientId && (
                      <span>
                        {t("edge.masterClientId")}:{" "}
                        <span className="text-ink">{edge.masterClientId}</span>
                      </span>
                    )}
                    {address && (
                      <span>
                        {t("edge.address")}:{" "}
                        <span className="font-[JetBrains_Mono,ui-monospace,monospace] text-ink">
                          {address}
                        </span>
                      </span>
                    )}
                  </div>
                )}
                {/* §17.1: касата държи действия, които облакът е отказал —
                    терминалите няма да се върнат в облака, докато човек не ги
                    разреши от портала на касата. */}
                {unresolved > 0 && (
                  <p className="m-0 text-[12px] font-semibold text-warn-ink">
                    {t("edge.hintUnresolved", { count: unresolved })}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {edge.cloudMode === "local" && (
                  <button
                    type="button"
                    className={ghostButtonClass}
                    title={t("edge.forceCloudHint")}
                    disabled={updateMutation.isPending}
                    onClick={() =>
                      updateMutation.mutate({
                        enabled: edge.enabled,
                        force_cloud: true,
                      })
                    }
                  >
                    {t("edge.forceCloud")}
                  </button>
                )}
                <button
                  type="button"
                  data-testid="edge-site-toggle"
                  className={edge.enabled ? ghostButtonClass : accentButtonClass}
                  disabled={updateMutation.isPending}
                  onClick={edge.enabled ? handleDisable : handleEnable}
                >
                  {edge.enabled ? t("edge.disable") : t("edge.enable")}
                </button>
              </div>
            </div>
          </section>

          {/* ── Това устройство ─────────────────────────────────────── */}
          <section
            className={`${sectionPad} flex flex-col gap-2.5 border-t border-shade-2`}
            data-testid="edge-device"
          >
            <SectionLabel>{t("edge.sectionDevice")}</SectionLabel>
            {deviceApplies ? (
              <div className={sectionRow}>
                <StateChip
                  tone={DEVICE_TONE[deviceState]}
                  label={deviceLabel}
                  testId="edge-device-state"
                />
                <p className="m-0 min-w-0 flex-1 text-[13px] leading-[1.45] text-body">
                  {deviceHint}
                </p>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {/* Акцентиран, докато не се знае, че ТОЗИ браузър стига до
                      касата: разрешението за локална мрежа се дава на всяко
                      устройство поотделно и това е мястото, откъдето се иска. */}
                  <button
                    type="button"
                    data-testid="edge-device-prepare"
                    className={
                      deviceState === "ready" ? ghostButtonClass : accentButtonClass
                    }
                    title={t("edge.prepareDeviceHint")}
                    onClick={handlePrepare}
                    disabled={preparing}
                  >
                    <ThunderboltOutlined />
                    {preparing ? t("edge.preparing") : t("edge.prepareDevice")}
                  </button>
                  <button
                    type="button"
                    className={ghostButtonClass}
                    onClick={handleCheck}
                    disabled={checking}
                  >
                    <ReloadOutlined className={checking ? "animate-spin" : ""} />
                    {checking ? t("edge.checking") : t("edge.check")}
                  </button>
                </div>
              </div>
            ) : (
              <p className="m-0 text-[12.5px] text-muted" data-testid="edge-device-waiting">
                {edge.enabled ? t("edge.prepareNoMaster") : t("edge.deviceNeedsSite")}
              </p>
            )}
          </section>
        </div>
      )}
    </ConfigPanel>
  );
}
